import { FC, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  useTheme,
  useMediaQuery,
  Slide
} from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";

interface Props {
  title: string;
  subtitle: string;
}

export const Notification: FC<Props> = ({ title, subtitle }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  return (
    <Box
      sx={{
        position: "fixed",
        top: theme.spacing(2),
        left: "50%",
        transform: "translateX(-50%)",
        ...(isMobile
          ? {
              maxWidth: "calc(100vw - 32px)",
              width: "calc(100vw - 32px)"
            }
          : {
              maxWidth: 500,
              width: "100%"
            }),
        zIndex: theme.zIndex.modal + 1
      }}
    >
      <Slide
        direction="down"
        in={isVisible}
        timeout={600}
        easing="cubic-bezier(0.4, 0, 0.2, 1)"
      >
        <Paper
          elevation={8}
          sx={{
            backgroundColor: theme.palette.primary.light,
            color: theme.palette.primary.contrastText,
            borderRadius: 2,
            overflow: "hidden",
            cursor: "pointer",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            transform: isVisible ? "scale(1)" : "scale(0.8)",
            opacity: isVisible ? 1 : 0,
            "&:hover": {
              transform: "translateY(-2px) scale(1.02)",
              boxShadow: theme.shadows[12]
            }
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
              padding: theme.spacing(2),
              gap: theme.spacing(1.5)
            }}
          >
            <InfoOutlined
              sx={{
                color: theme.palette.primary.contrastText,
                fontSize: 24,
                flexShrink: 0,
                marginTop: 0.5
              }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: "1.1rem",
                  lineHeight: 1.3,
                  marginBottom: theme.spacing(0.5),
                  color: theme.palette.primary.contrastText
                }}
              >
                {title}
              </Typography>

              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.95rem",
                    lineHeight: 1.4,
                    color: theme.palette.primary.contrastText,
                    opacity: 0.9,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word"
                  }}
                >
                  {subtitle}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              height: 4,
              backgroundColor: theme.palette.primary.dark,
              position: "relative",
              overflow: "hidden"
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                height: "100%",
                backgroundColor: theme.palette.primary.contrastText,
                opacity: 0.3,
                width: "100%",
                transition: "width 0.3s ease-in-out"
              }}
            />
          </Box>
        </Paper>
      </Slide>
    </Box>
  );
};
